/**
 * Écran des réglages, à deux niveaux.
 *
 * Reprend `activity_settings.xml` : une liste de catégories, puis le détail
 * d'une seule catégorie à la fois, avec le titre de la barre qui suit la
 * navigation. Les plages des curseurs sont celles du fichier de layout.
 */

import { createIcon } from './icons'
import { chooseDialog, confirmDialog, showDialog } from './dialogs'
import { choiceRow, sliderRow, switchRow, textField } from './controls'
import type { PreviewHud } from './liveHud'
import type { Translator } from '../i18n'
import type { StringKey } from '../i18n'
import type { CacheCounts } from '../core/storage'
import { MAP_STYLES, getStyleDescriptor } from '../core/mapStyles'
import { PUBLISHER } from '../core/publisher'
import {
  MARKER_TYPE_DERIVATION,
  PREDEFINED_MARKER_TYPES,
} from '../core/types'
import {
  locationIconSize,
  MAX_SYMBOL_LENGTH,
  SUPPORTED_RADAR_COUNTRIES,
  type Preferences,
  type PreferencesStore,
} from '../core/preferences'

interface CategoryDefinition {
  readonly id: string
  readonly titleKey: StringKey
  readonly descKey: StringKey
  readonly build: (body: HTMLElement) => void
}

export interface SettingsCallbacks {
  /** Résolu une fois la purge terminée, pour que l'écran relise un vrai total. */
  onClearCache(): Promise<void>
  /**
   * Espace occupé par l'application, en octets.
   *
   * `null` quand le navigateur ne l'estime pas. C'est le seul chiffre en
   * octets disponible : le navigateur ne pèse pas ses caches séparément, d'où
   * le détail en nombre d'entrées que rend `cacheCounts`.
   */
  storageUsage(): Promise<number | null>
  /** Entrées retenues par nature — tuiles, itinéraires, radars, altitudes. */
  cacheCounts(): Promise<CacheCounts>
  onPreferencesChanged(): void
  /**
   * Montre un panneau garni de données fictives, par-dessus la carte.
   *
   * L'écran des réglages se referme d'abord : les panneaux se dessinent sur la
   * carte, qu'il recouvre entièrement.
   */
  showHudPreview(kind: PreviewHud | 'all'): void
  /**
   * Avertissement légal, à obtenir avant toute activation des radars.
   *
   * @returns `false` si l'utilisateur refuse, auquel cas l'interrupteur
   *   revient à sa position précédente.
   */
  confirmRadars(): Promise<boolean>
  /** Télécharge les pays retenus et renvoie le nombre de radars obtenus. */
  refreshRadars(options: {
    force: boolean
    onProgress: (label: string) => void
  }): Promise<{ count: number; failed: readonly string[] }>
}

export class SettingsScreen {
  readonly element: HTMLElement

  readonly #title: HTMLElement
  readonly #scroll: HTMLElement
  readonly #back: HTMLButtonElement

  #categories: CategoryDefinition[] = []
  #currentCategory: string | null = null

  constructor(
    container: HTMLElement,
    private readonly t: Translator,
    private readonly prefs: PreferencesStore,
    private readonly callbacks: SettingsCallbacks,
    private readonly version: string,
  ) {
    this.element = document.createElement('section')
    this.element.className = 'settings'
    this.element.dataset.open = 'false'
    this.element.setAttribute('aria-label', t.t('settings'))

    const toolbar = document.createElement('div')
    toolbar.className = 'settings__toolbar'

    this.#back = document.createElement('button')
    this.#back.type = 'button'
    this.#back.className = 'settings__back'
    this.#back.setAttribute('aria-label', t.t('close'))
    this.#back.append(createIcon('back'))
    this.#back.addEventListener('click', () => this.goBack())

    this.#title = document.createElement('h1')
    this.#title.className = 'settings__title'
    this.#title.textContent = t.t('settings')

    toolbar.append(this.#back, this.#title)

    this.#scroll = document.createElement('div')
    this.#scroll.className = 'settings__scroll'

    this.element.append(toolbar, this.#scroll)
    container.append(this.element)

    this.#defineCategories()
    this.showCategoryList()
  }

  open(): void {
    this.element.dataset.open = 'true'
    this.showCategoryList()
  }

  close(): void {
    this.element.dataset.open = 'false'
  }

  get isOpen(): boolean {
    return this.element.dataset.open === 'true'
  }

  /** Retour : d'une catégorie vers la liste, puis de la liste vers la carte. */
  goBack(): void {
    if (this.#currentCategory !== null) {
      this.showCategoryList()
      return
    }
    this.close()
  }

  showCategoryList(): void {
    this.#currentCategory = null
    this.#title.textContent = this.t.t('settings')

    const list = document.createElement('div')

    for (const category of this.#categories) {
      const entry = document.createElement('button')
      entry.type = 'button'
      entry.className = 'settings-category'

      const title = document.createElement('div')
      title.className = 'settings-category__title'
      title.textContent = this.t.t(category.titleKey)

      const desc = document.createElement('div')
      desc.className = 'settings-category__desc'
      desc.textContent = this.t.t(category.descKey)

      entry.append(title, desc)
      entry.addEventListener('click', () => this.#showCategory(category))
      list.append(entry)
    }

    this.#scroll.replaceChildren(list)
  }

  #showCategory(category: CategoryDefinition): void {
    this.#currentCategory = category.id
    this.#title.textContent = this.t.t(category.titleKey)

    const body = document.createElement('div')
    category.build(body)
    this.#scroll.replaceChildren(body)
  }

  /** Redessine la catégorie ouverte, après un changement qui la modifie. */
  #refresh(): void {
    const category = this.#categories.find((c) => c.id === this.#currentCategory)
    if (category) this.#showCategory(category)
  }

  /* -------------------------------------------------------------------
   * Fabriques de contrôles
   * ---------------------------------------------------------------- */

  #switchRow(
    labelKey: StringKey,
    key: keyof Preferences,
    onChange?: () => void,
  ): HTMLElement {
    return switchRow({
      label: this.t.t(labelKey),
      get: () => this.prefs.get(key) as boolean,
      set: (value) => this.prefs.set(key, value as never),
      onChange: () => {
        this.callbacks.onPreferencesChanged()
        onChange?.()
      },
    })
  }

  #sliderRow(
    labelKey: StringKey,
    key: keyof Preferences,
    config: {
      min: number
      max: number
      step: number
      format: (value: number) => string
    },
  ): HTMLElement {
    return sliderRow({
      label: this.t.t(labelKey),
      min: config.min,
      max: config.max,
      step: config.step,
      format: config.format,
      get: () => this.prefs.get(key) as number,
      set: (value) => this.prefs.set(key, value as never),
      onChange: () => this.callbacks.onPreferencesChanged(),
    })
  }

  #choiceRow(
    labelKey: StringKey,
    currentLabel: string,
    onOpen: () => void,
  ): HTMLElement {
    return choiceRow({ label: this.t.t(labelKey), value: currentLabel, onOpen })
  }

  /**
   * Bouton d'aperçu.
   *
   * L'écran se referme avant de rendre la main : les panneaux se dessinent sur
   * la carte, que les réglages recouvrent entièrement. Android fait de même en
   * terminant son activité de réglages.
   */
  #previewButton(kind: PreviewHud | 'all'): HTMLElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'button-tonal setting-preview'
    button.dataset.preview = kind
    button.textContent =
      kind === 'all' ? this.t.t('hud_preview_all') : this.t.t('hud_preview')
    button.addEventListener('click', () => {
      this.close()
      this.callbacks.showHudPreview(kind)
    })
    return button
  }

  #blockTitle(text: string): HTMLElement {
    const element = document.createElement('div')
    element.className = 'settings-block-title'
    element.textContent = text
    return element
  }

  #blockBody(text: string): HTMLElement {
    const element = document.createElement('div')
    element.className = 'settings-block-body'
    element.textContent = text
    return element
  }

  /* -------------------------------------------------------------------
   * Définition des catégories
   * ---------------------------------------------------------------- */

  #defineCategories(): void {
    this.#categories = [
      {
        id: 'general',
        titleKey: 'settings_section_general',
        descKey: 'settings_cat_general_desc',
        build: (body) => {
          const languageLabels = {
            system: this.t.t('language_system'),
            fr: this.t.t('language_french'),
            en: this.t.t('language_english'),
          } as const

          body.append(
            this.#choiceRow(
              'language',
              languageLabels[this.prefs.get('language')],
              () =>
                chooseDialog(document.body, {
                  title: this.t.t('language'),
                  cancelLabel: this.t.t('cancel'),
                  choices: (['system', 'fr', 'en'] as const).map((value) => ({
                    value,
                    label: languageLabels[value],
                    selected: this.prefs.get('language') === value,
                  })),
                  onChoose: (value) => {
                    this.prefs.set('language', value)
                    this.callbacks.onPreferencesChanged()
                    this.#refresh()
                  },
                }),
            ),
          )

          const themeLabels = {
            system: this.t.t('theme_system'),
            light: this.t.t('theme_light'),
            dark: this.t.t('theme_dark'),
          } as const

          body.append(
            this.#choiceRow('theme', themeLabels[this.prefs.get('theme')], () =>
              chooseDialog(document.body, {
                title: this.t.t('theme'),
                cancelLabel: this.t.t('cancel'),
                choices: (['system', 'light', 'dark'] as const).map((value) => ({
                  value,
                  label: themeLabels[value],
                  selected: this.prefs.get('theme') === value,
                })),
                onChoose: (value) => {
                  this.prefs.set('theme', value)
                  this.callbacks.onPreferencesChanged()
                  this.#refresh()
                },
              }),
            ),
          )
        },
      },

      {
        id: 'map',
        titleKey: 'settings_section_map',
        descKey: 'settings_cat_map_desc',
        build: (body) => {
          const current = getStyleDescriptor(this.prefs.get('mapStyle'))

          body.append(
            this.#choiceRow(
              'map_style',
              `${current.label} (${current.provider})`,
              () =>
                chooseDialog(document.body, {
                  title: this.t.t('map_style'),
                  cancelLabel: this.t.t('cancel'),
                  choices: MAP_STYLES.map((style) => ({
                    value: style.id,
                    label: `${style.label} (${style.provider})`,
                    selected: style.id === current.id,
                  })),
                  onChoose: (value) => {
                    this.prefs.set('mapStyle', value)
                    this.callbacks.onPreferencesChanged()
                    this.#refresh()
                  },
                }),
            ),
          )

          body.append(this.#switchRow('show_3d_buildings', 'show3dBuildings'))

          // Le relief n'existe que dans le style Liberty : on l'annonce plutôt
          // que de laisser un interrupteur sans effet visible.
          const summary = document.createElement('div')
          summary.className = 'setting-row__summary'
          summary.textContent = current.supports3dBuildings
            ? this.t.t('show_3d_buildings_summary_available')
            : this.t.t('show_3d_buildings_summary_unavailable')
          body.append(summary)
        },
      },

      {
        id: 'interface',
        titleKey: 'settings_section_interface',
        descKey: 'settings_cat_interface_desc',
        build: (body) => {
          body.append(
            this.#switchRow('show_zoom_buttons', 'showZoomButtons'),
            this.#switchRow('show_guidance_button', 'showGuidanceButton'),
            this.#sliderRow('location_icon_size', 'locationIconSize', {
              min: 16,
              max: 48,
              step: 2,
              format: (v) => `${locationIconSize(v)} px`,
            }),
            this.#switchRow('show_speed_altitude', 'showSpeedAltitude', () =>
              this.#refresh(),
            ),
          )

          if (this.prefs.get('showSpeedAltitude')) {
            const sub = document.createElement('div')
            sub.className = 'setting-sub'
            sub.append(
              this.#switchRow('show_altitude', 'showAltitude'),
              this.#sliderRow('speed_altitude_size', 'speedAltitudeSize', {
                min: 0,
                max: 4,
                step: 1,
                format: (v) => String(v),
              }),
            )
            body.append(sub)
          }
        },
      },

      {
        id: 'navigation',
        titleKey: 'settings_section_navigation',
        descKey: 'settings_cat_navigation_desc',
        build: (body) => {
          body.append(
            this.#sliderRow('relock_delay_label', 'relockDelay', {
              min: 5,
              max: 60,
              step: 5,
              format: (v) => this.t.t('relock_delay_value', { value: v }),
            }),
            this.#switchRow('km_indicator_label', 'kmIndicatorEnabled', () =>
              this.#refresh(),
            ),
          )

          // Le bouton n'apparaît que si le panneau est activé, comme Android :
          // proposer l'aperçu d'un panneau qu'on vient d'éteindre laisserait
          // croire qu'il se rallume.
          if (this.prefs.get('kmIndicatorEnabled')) {
            body.append(this.#previewButton('km'))
          }

          body.append(
            this.#switchRow('roadbook_label', 'roadbookEnabled', () =>
              this.#refresh(),
            ),
          )

          if (this.prefs.get('roadbookEnabled')) {
            const sub = document.createElement('div')
            sub.className = 'setting-sub'
            sub.append(
              this.#sliderRow(
                'roadbook_marker_count_portrait',
                'roadbookMarkerCountPortrait',
                { min: 1, max: 10, step: 1, format: (v) => String(v) },
              ),
              this.#sliderRow(
                'roadbook_marker_count_landscape',
                'roadbookMarkerCountLandscape',
                { min: 1, max: 10, step: 1, format: (v) => String(v) },
              ),
              this.#sliderRow('roadbook_text_size', 'roadbookTextSize', {
                min: 0,
                max: 4,
                step: 1,
                format: (v) => String(v),
              }),
              this.#sliderRow(
                'roadbook_disappear_distance',
                'roadbookDisappearDistance',
                {
                  min: 0,
                  max: 5,
                  step: 0.5,
                  format: (v) => `${this.t.number(v, 1)} km`,
                },
              ),
              this.#previewButton('roadbook'),
            )
            body.append(sub)
          }

          body.append(
            this.#switchRow('marker_alert_label', 'markerAlertEnabled', () =>
              this.#refresh(),
            ),
          )

          if (this.prefs.get('markerAlertEnabled')) {
            const sub = document.createElement('div')
            sub.className = 'setting-sub'
            sub.append(
              this.#sliderRow('marker_alert_distance', 'markerAlertDistance', {
                min: 0.5,
                max: 10,
                step: 0.5,
                format: (v) => `${this.t.number(v, 1)} km`,
              }),
              this.#sliderRow('marker_alert_size', 'markerAlertSize', {
                min: 0,
                max: 4,
                step: 1,
                format: (v) => String(v),
              }),
              this.#previewButton('alert'),
            )
            body.append(sub)
          }

          body.append(
            this.#switchRow(
              'elevation_profile_hud_label',
              'elevationProfileHudEnabled',
              () => this.#refresh(),
            ),
          )

          if (this.prefs.get('elevationProfileHudEnabled')) {
            const sub = document.createElement('div')
            sub.className = 'setting-sub'
            sub.append(
              this.#sliderRow(
                'elevation_profile_hud_range',
                'elevationProfileHudRange',
                {
                  min: 1,
                  max: 20,
                  step: 1,
                  format: (v) => `${this.t.number(v, 1)} km`,
                },
              ),
              this.#sliderRow(
                'elevation_profile_hud_range_behind',
                'elevationProfileHudRangeBehind',
                {
                  min: 0,
                  max: 10,
                  step: 1,
                  format: (v) => `${this.t.number(v, 1)} km`,
                },
              ),
              this.#previewButton('profile'),
            )
            body.append(sub)
          }

          // La démo montre les quatre ensemble, réglages d'affichage compris —
          // c'est ainsi qu'on voit s'ils se gênent, ce qu'un aperçu isolé ne
          // dira jamais. Sans condition d'activation : elle démontre les
          // panneaux, elle n'en reflète pas l'état.
          const divider = document.createElement('div')
          divider.className = 'setting-divider'
          body.append(divider, this.#previewButton('all'))
        },
      },

      {
        id: 'markers',
        titleKey: 'settings_section_markers',
        descKey: 'settings_cat_markers_desc',
        build: (body) => {
          body.append(
            this.#switchRow('show_km_markers', 'kmMarkersEnabled'),
            this.#sliderRow('km_marker_interval_label', 'kmMarkerInterval', {
              min: 0.5,
              max: 10,
              step: 0.5,
              format: (v) => `${this.t.number(v, 1)} km`,
            }),
          )

          const divider = document.createElement('div')
          divider.className = 'setting-divider'
          body.append(divider)

          body.append(
            // L'échelle est affichée en pourcentage d'une valeur de référence
            // de 0,6 : « 100 % » parle davantage que « 0,6 ».
            this.#sliderRow('marker_size_label', 'markerScale', {
              min: 0.4,
              max: 1.2,
              step: 0.05,
              format: (v) => `${Math.round((v / 0.6) * 100)} %`,
            }),
            this.#switchRow('custom_marker_dark_mode', 'customMarkerDarkMode'),
            this.#choiceRow('predefined_markers', '', () =>
              this.#showPredefinedSymbols(),
            ),
          )
        },
      },

      {
        id: 'radars',
        titleKey: 'radars_section',
        descKey: 'settings_cat_radars_desc',
        build: (body) => {
          // L'interrupteur n'est pas confié au constructeur générique : son
          // activation passe d'abord par un avertissement, et un refus doit
          // le ramener en arrière.
          body.append(
            switchRow({
              label: this.t.t('radars_enable'),
              get: () => this.prefs.get('radarsEnabled'),
              set: () => {},
              onChange: () => {},
            }),
          )

          const toggle = body.querySelector<HTMLInputElement>(
            '.setting-row input[type="checkbox"]',
          )
          toggle?.addEventListener('change', () => {
            void this.#onRadarsToggled(toggle)
          })

          const selected = this.prefs.get('radarCountries')

          const countryRow = this.#choiceRow(
            'radars_countries',
            selected.map((code) => countryName(this.t.locale, code)).join(', '),
            () => this.#showCountryPicker(),
          )
          body.append(countryRow)

          const status = document.createElement('div')
          status.className = 'setting-row__summary'
          body.append(status)

          const refresh = document.createElement('button')
          refresh.type = 'button'
          refresh.className = 'button button--outlined'
          refresh.textContent = this.t.t('radars_refresh')
          body.append(refresh)

          const showLastRefresh = (): void => {
            const last = this.prefs.get('radarsLastRefresh')
            status.textContent =
              last > 0
                ? this.t.t('radars_last_refresh', {
                    date: this.t.dateTime(last),
                  })
                : this.t.t('radars_never_refreshed')
          }
          showLastRefresh()

          refresh.addEventListener('click', () => {
            void this.#runRadarRefresh(refresh, countryRow, status, {
              force: true,
              onDone: showLastRefresh,
            })
          })
        },
      },

      {
        id: 'about',
        titleKey: 'settings_section_about',
        descKey: 'settings_cat_about_desc',
        build: (body) => {
          const name = document.createElement('div')
          name.style.fontSize = '28px'
          name.style.padding = '8px 0 12px'
          name.textContent = this.t.t('app_name')
          body.append(name)

          body.append(
            this.#blockTitle(this.t.t('about_version_label')),
            this.#blockBody(this.version),
            this.#blockTitle(this.t.t('about_author_label')),
            this.#blockBody('Corentin Jacquemet'),
            // Entre l'auteur et la vie privée, comme dans
            // `activity_settings.xml` : le texte est affiché en permanence,
            // jamais replié derrière un lien.
            this.#blockTitle(this.t.t('about_license_title')),
            this.#blockBody(this.t.t('license_text')),
            this.#blockTitle(this.t.t('about_privacy_title')),
            this.#blockBody(this.t.t('about_privacy_text')),
            // Mentions légales et politique de confidentialité, dues dès que le
            // site est public. Les valeurs manquantes viennent de `PUBLISHER`,
            // qui les rassemble en un point ; `scripts/check-release.sh` refuse
            // une publication tant qu'il en reste.
            this.#blockTitle(this.t.t('about_legal_title')),
            this.#blockBody(this.t.t('about_legal_text', { ...PUBLISHER })),
            this.#blockTitle(this.t.t('about_gdpr_title')),
            this.#blockBody(this.t.t('about_gdpr_text', { ...PUBLISHER })),
            this.#blockTitle(this.t.t('about_components_title')),
            this.#blockBody(
              [
                'MapLibre GL JS',
                'OpenFreeMap',
                'VersaTiles',
                'Esri, Maxar, Earthstar Geographics',
                'OSRM',
                'EGM96',
              ].join(' · '),
            ),
            this.#blockTitle(this.t.t('about_data_sources_title')),
            this.#blockBody(
              [
                '© OpenStreetMap contributors',
                this.t.locale === 'fr'
                  ? 'Radars : OpenStreetMap (ODbL)'
                  : 'Speed cameras: OpenStreetMap (ODbL)',
                this.t.locale === 'fr'
                  ? 'Altitude : OpenTopoData, Open-Meteo'
                  : 'Elevation: OpenTopoData, Open-Meteo',
              ].join('\n'),
            ),
          )

          body.append(this.#blockTitle(this.t.t('about_storage_title')))

          // L'estimation arrive de façon asynchrone : la ligne est posée
          // d'abord, remplie ensuite. Attendre pour afficher la catégorie
          // entière la ferait clignoter à chaque ouverture.
          const usage = this.#blockBody('…')
          body.append(usage)

          // Quatre lignes comme Android, mais en **nombre d'entrées** et non
          // en octets : le navigateur ne pèse pas ses caches séparément, et
          // les relire un par un — jusqu'à 3 000 tuiles — rendrait la section
          // inouvrable. Écart consigné au tableau du `README.md`.
          const detail = this.#blockBody('')
          detail.style.whiteSpace = 'pre-line'
          body.append(detail)

          const showUsage = async (): Promise<void> => {
            const bytes = await this.callbacks.storageUsage()
            usage.textContent =
              bytes === null
                ? this.t.t('about_storage_unknown')
                : this.t.fileSize(bytes)
          }

          const showDetail = async (): Promise<void> => {
            const counts = await this.callbacks.cacheCounts()
            // Sans séparateur de milliers, trois mille tuiles se lisent mal.
            const entries = (value: number): string => this.t.number(value, 0)

            detail.textContent = [
              this.t.t('about_cache_tiles', { value: entries(counts.tiles) }),
              this.t.t('about_cache_routes', { value: entries(counts.routes) }),
              this.t.t('about_cache_radars', { value: entries(counts.radars) }),
              this.t.t('about_cache_elevations', {
                value: entries(counts.elevations),
              }),
            ].join('\n')
          }

          void showUsage()
          void showDetail()

          // Le chiffre ne peut pas servir de retour à la purge : mesuré, le
          // navigateur ne rafraîchit son estimation qu'au bout de plusieurs
          // dizaines de secondes, et un utilisateur qui vide son cache le
          // voyait inchangé — d'où le bouton réputé sans effet. Le dire ici,
          // et confirmer la purge par un message, plutôt que d'attendre une
          // valeur qui ne viendra pas.
          const note = this.#blockBody(this.t.t('about_storage_estimate_note'))
          note.style.opacity = '0.7'
          body.append(note)

          const clear = document.createElement('button')
          clear.type = 'button'
          clear.className = 'button-tonal'
          clear.style.marginTop = '8px'
          clear.textContent = this.t.t('about_clear_cache')
          clear.addEventListener('click', () => {
            // Ce qui part ne se récupère qu'au réseau, et la base de radars
            // coûte plusieurs minutes : l'utilisateur doit savoir avant, pas
            // le découvrir à la sortie suivante.
            confirmDialog(document.body, {
              title: this.t.t('about_clear_cache_confirm_title'),
              message: this.t.t('about_clear_cache_confirm_message'),
              confirmLabel: this.t.t('about_clear_cache'),
              cancelLabel: this.t.t('cancel'),
              onConfirm: () => {
                // Désarmé le temps de la purge : un second clic relancerait
                // tout, et le chiffre affiché viendrait de la course entre les
                // deux.
                clear.disabled = true
                void this.callbacks
                  .onClearCache()
                  // Le détail, lui, retombe à zéro tout de suite : c'est le
                  // retour immédiat que le total ne sait pas donner.
                  .then(() => Promise.all([showUsage(), showDetail()]))
                  .finally(() => {
                    clear.disabled = false
                  })
              },
            })
          })
          body.append(clear)
        },
      },
    ]
  }

  /**
   * Sélection des pays de la base de radars.
   *
   * Au moins un pays doit rester coché : une base vide n'a rien à rafraîchir.
   */
  /**
   * Bascule de l'affichage des radars.
   *
   * L'activation est soumise à l'avertissement légal ; un refus ramène
   * l'interrupteur en arrière plutôt que de laisser croire à une activation.
   */
  async #onRadarsToggled(input: HTMLInputElement): Promise<void> {
    if (!input.checked) {
      this.prefs.set('radarsEnabled', false)
      this.callbacks.onPreferencesChanged()
      return
    }

    const accepted = await this.callbacks.confirmRadars()
    if (!accepted) {
      input.checked = false
      return
    }

    this.prefs.set('radarsEnabled', true)
    this.callbacks.onPreferencesChanged()
  }

  /**
   * Téléchargement de la base, avec sa progression.
   *
   * Le bouton et la ligne des pays sont neutralisés pendant l'opération :
   * relancer un téléchargement ou changer de pays en cours de route
   * produirait deux séries concurrentes sur un service public à quota.
   */
  async #runRadarRefresh(
    button: HTMLButtonElement,
    countryRow: HTMLElement,
    status: HTMLElement,
    options: { force: boolean; onDone: () => void },
  ): Promise<void> {
    button.disabled = true
    countryRow.setAttribute('aria-disabled', 'true')
    countryRow.style.pointerEvents = 'none'
    countryRow.style.opacity = '0.5'

    try {
      const result = await this.callbacks.refreshRadars({
        force: options.force,
        onProgress: (label) => {
          status.textContent = label
        },
      })

      // Nommer les pays en échec plutôt qu'un « échec » nu : c'est ce qui
      // rend le bouton actionnable, il ne retentera que ceux-là.
      status.textContent =
        result.failed.length > 0
          ? `${this.t.t('radars_refresh_error')} — ${result.failed
              .map((code) => countryName(this.t.locale, code))
              .join(', ')}`
          : this.t.t('radars_refresh_ok', { count: result.count })

      // Le récapitulatif final laisse la place à la date au bout d'un moment :
      // c'est elle qui a de la valeur une fois l'opération oubliée.
      window.setTimeout(options.onDone, 4000)
    } finally {
      button.disabled = false
      countryRow.removeAttribute('aria-disabled')
      countryRow.style.pointerEvents = ''
      countryRow.style.opacity = ''
    }
  }

  /**
   * Symboles des types prédéfinis.
   *
   * Le symbole choisi ici est celui que prendront les prochains marqueurs du
   * type ; ceux déjà posés gardent le leur, comme sur Android — le symbole est
   * une donnée du marqueur, pas une lecture du réglage.
   *
   * La dérivation ne figure pas dans la liste : son symbole n'est jamais saisi
   * mais déduit du côté choisi (↰ ou ↱), une surcharge y resterait sans effet.
   * Android l'y laisse malgré tout, et le réglage n'y sert à rien non plus.
   */
  #showPredefinedSymbols(): void {
    const list = document.createElement('div')

    const rebuild = (): void => {
      const rows = PREDEFINED_MARKER_TYPES.filter(
        (type) => type.id !== MARKER_TYPE_DERIVATION,
      ).map((type) => {
        const row = document.createElement('div')
        row.className = 'setting-row'

        const label = document.createElement('span')
        label.className = 'setting-row__label'
        const symbol = this.prefs.symbolForType(type.id)
        label.textContent = `${symbol}  ${this.t.t(type.labelKey as StringKey)}`

        const edit = document.createElement('button')
        edit.type = 'button'
        edit.className = 'icon-button'
        const editLabel = this.t.t('edit_symbol', {
          name: this.t.t(type.labelKey as StringKey),
        })
        edit.title = editLabel
        edit.setAttribute('aria-label', editLabel)
        edit.append(createIcon('edit', 20))
        edit.addEventListener('click', () =>
          this.#editSymbol(type.id, type.defaultSymbol, editLabel, rebuild),
        )

        row.append(label, edit)
        return row
      })

      list.replaceChildren(...rows)
    }

    rebuild()

    showDialog(document.body, {
      title: this.t.t('predefined_markers'),
      body: list,
      dismissible: true,
      actions: [{ label: this.t.t('ok'), primary: true }],
    })
  }

  /** Saisie d'un symbole, avec retour au symbole d'origine. */
  #editSymbol(
    typeId: string,
    defaultSymbol: string,
    title: string,
    onDone: () => void,
  ): void {
    const { element, input } = textField({
      label: this.t.t('marker_symbol'),
      value: this.prefs.symbolForType(typeId),
      hint: this.t.t('marker_symbol_hint'),
    })
    input.maxLength = MAX_SYMBOL_LENGTH

    // La surcharge est retirée plutôt qu'écrite à la valeur d'origine : le type
    // suivra ainsi un changement du symbole par défaut dans une version
    // ultérieure, ce qu'une copie figée empêcherait.
    const write = (symbol: string | null): void => {
      const overrides = { ...this.prefs.get('predefinedSymbols') }
      if (symbol === null) delete overrides[typeId]
      else overrides[typeId] = symbol
      this.prefs.set('predefinedSymbols', overrides)
      this.callbacks.onPreferencesChanged()
      onDone()
    }

    showDialog(document.body, {
      title,
      body: element,
      dismissible: true,
      actions: [
        { label: this.t.t('cancel') },
        { label: this.t.t('reset'), onClick: () => write(null) },
        {
          label: this.t.t('ok'),
          primary: true,
          onClick: () => {
            const symbol = input.value.trim()
            // Un champ vidé vaut un retour au symbole d'origine : un marqueur
            // sans symbole ne se distinguerait plus sur la carte.
            write(symbol.length === 0 || symbol === defaultSymbol ? null : symbol)
          },
        },
      ],
    })
  }

  #showCountryPicker(): void {
    const selected = new Set(this.prefs.get('radarCountries'))

    const list = document.createElement('div')

    for (const code of SUPPORTED_RADAR_COUNTRIES) {
      const row = document.createElement('label')
      row.className = 'dialog__option'

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.checked = selected.has(code)
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) selected.add(code)
        else selected.delete(code)
      })

      const label = document.createElement('span')
      label.textContent = countryName(this.t.locale, code)

      row.append(checkbox, label)
      list.append(row)
    }

    showDialog(document.body, {
      title: this.t.t('radars_countries'),
      body: list,
      actions: [
        { label: this.t.t('cancel') },
        {
          label: this.t.t('ok'),
          onClick: () => {
            this.prefs.set('radarCountries', [...selected])
            this.callbacks.onPreferencesChanged()
            this.#refresh()
          },
        },
      ],
    })
  }
}

/**
 * Nom d'un pays dans la langue de l'interface.
 *
 * Traduit par le navigateur plutôt que par nos fichiers de chaînes : la liste
 * des pays pris en charge peut grandir sans rien à traduire.
 */
function countryName(locale: string, code: string): string {
  return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code
}
